// LastPromptsV2 — v2-styled prompt history affordance.
//
// Renders an IconButton that opens a Popover listing recent prompts the
// user has sent. Clicking an entry calls `onPromptSelected(text, json?)`,
// which PromptV2 wires to fill its textarea (without auto-sending).
//
// Source of truth is `storageService.getPrompts()`. We re-read on each
// `usePromptState` change (so a freshly sent prompt appears in the list
// without remounting the component).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useAiChatServices } from '../AiChat/AiChatContext';
import { usePromptState } from '../../services/storageService';
import type { Prompt } from '../../models/Prompt';
import { HistoryIcon, TrashIcon } from './icons';

export interface LastPromptsV2Props {
    /** Receives the selected prompt's text (and optional JSON payload). */
    onPromptSelected: (promptText: string, promptJson?: string) => void;
}

const MAX_VISIBLE = 12;

export const LastPromptsV2: React.FC<LastPromptsV2Props> = ({ onPromptSelected }) => {
    const { storageService } = useAiChatServices();
    const { promptState } = usePromptState(storageService);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        setPrompts(storageService.getPrompts());
    }, [promptState, storageService]);

    const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => setAnchorEl(null), []);

    const handlePick = useCallback(
        (prompt: Prompt) => {
            onPromptSelected(prompt.promptText, prompt.promptJson);
            setAnchorEl(null);
        },
        [onPromptSelected],
    );

    const handleClearAll = useCallback(() => {
        storageService.clearAllPrompts();
        setPrompts([]);
    }, [storageService]);

    const visible = useMemo(() => prompts.slice(0, MAX_VISIBLE), [prompts]);

    // Hide entirely when there's no history to surface.
    if (prompts.length === 0) return null;

    const open = !!anchorEl;

    return (
        <>
            <Tooltip title="Histórico de prompts" placement="top">
                <IconButton
                    ref={triggerRef}
                    size="small"
                    onClick={handleOpen}
                    sx={(theme) => ({
                        color: open ? theme.palette.text.primary : theme.palette.text.secondary,
                        p: 0.75,
                        '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.background.paper },
                    })}
                    aria-label="Histórico de prompts"
                >
                    <HistoryIcon size={16} />
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: (theme) => ({
                            backgroundColor: theme.palette.surface.raised,
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 0.5,
                            width: 380,
                            maxWidth: '90vw',
                            overflow: 'hidden',
                        }),
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1,
                        borderBottom: `1px solid ${theme.palette.border.soft}`,
                    })}
                >
                    <Typography
                        sx={(theme) => ({
                            fontSize: 10,
                            color: theme.palette.text.disabled,
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            fontWeight: 500,
                        })}
                    >
                        Últimos {prompts.length} prompt{prompts.length === 1 ? '' : 's'}
                    </Typography>
                    <Button
                        size="small"
                        onClick={handleClearAll}
                        startIcon={<TrashIcon size={12} />}
                        sx={(theme) => ({
                            fontSize: 10,
                            color: theme.palette.text.secondary,
                            textTransform: 'none',
                            minWidth: 0,
                            px: 0.75,
                            py: 0.25,
                            '&:hover': { color: theme.palette.secondary.main, backgroundColor: theme.palette.surface.hover },
                        })}
                    >
                        Limpar
                    </Button>
                </Box>

                {/* List */}
                <Box
                    sx={{
                        maxHeight: '40vh',
                        overflowY: 'auto',
                        py: 0.5,
                    }}
                >
                    {visible.map((prompt, index) => {
                        const number = prompts.length - index;
                        return (
                            <Box
                                key={prompt.id ?? `${index}-${prompt.created}`}
                                onClick={() => handlePick(prompt)}
                                sx={(theme) => ({
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.85,
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${theme.palette.border.soft}`,
                                    '&:last-of-type': { borderBottom: 'none' },
                                    '&:hover': { backgroundColor: theme.palette.surface.hover },
                                })}
                            >
                                <Typography
                                    sx={(theme) => ({
                                        fontFamily: theme.typography.fontFamilyMono,
                                        fontSize: 10,
                                        color: theme.palette.text.disabled,
                                        minWidth: 28,
                                        textAlign: 'right',
                                        flex: '0 0 auto',
                                        pt: 0.2,
                                    })}
                                >
                                    #{number}
                                </Typography>
                                <Typography
                                    sx={(theme) => ({
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: 12,
                                        lineHeight: 1.4,
                                        color: theme.palette.text.primary,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    })}
                                >
                                    {prompt.promptText}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {prompts.length > MAX_VISIBLE && (
                    <Box
                        sx={(theme) => ({
                            px: 1.5,
                            py: 0.75,
                            borderTop: `1px solid ${theme.palette.border.soft}`,
                            fontSize: 10,
                            color: theme.palette.text.disabled,
                            textAlign: 'center',
                            fontFamily: theme.typography.fontFamilyMono,
                        })}
                    >
                        +{prompts.length - MAX_VISIBLE} mais armazenados
                    </Box>
                )}
            </Popover>
        </>
    );
};

LastPromptsV2.displayName = 'LastPromptsV2';
